import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { BookOpen, Rocket, BarChart, Flag, Trophy } from "lucide-react";

const phasesConfig = [
  { icon: BookOpen, color: "text-blue-500" },
  { icon: Rocket, color: "text-orange-500" },
  { icon: BarChart, color: "text-emerald-500" },
  { icon: Flag, color: "text-purple-500" },
  { icon: Trophy, color: "text-amber-500" }
];

export default function Timeline() {
  const { t } = useTranslation("landing");
  const [active, setActive] = useState(0);

  // Load phases titles, details and bottom highlights
  const phasesData = t("timeline.phases", { returnObjects: true }) as any[] || [];
  const bottom = t("timeline.bottom", { returnObjects: true }) as string[] || [];

  const activePhase = phasesData[active] || { title: "", sub: "", items: [] };
  const ActiveIcon = phasesConfig[active]?.icon || BookOpen;
  const activeColor = phasesConfig[active]?.color || "text-primary";

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center">
          <p className="label-eyebrow text-center mb-3">
            {t("timeline.tag", "THE CURRICULUM")}
          </p>
          <h2 className="text-4xl font-extrabold text-navy sm:text-5xl">
            {t("timeline.title", "4 Week Journey")}
          </h2>
        </div>

        {/* Phase Selectors */}
        <div className="mt-12 flex flex-wrap justify-center gap-2">
          {phasesConfig.map((p, i) => {
            const isActive = active === i;
            const Icon = p.icon;
            return (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all border border-slate-200/60 cursor-pointer flex items-center gap-2 ${
                  isActive
                    ? "bg-primary text-white border-primary shadow-glow scale-105"
                    : "bg-surface text-slate-body hover:bg-blue-bg hover:text-navy"
                }`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-white" : p.color}`} />
                <span>Week {i}</span>
              </button>
            );
          })}
        </div>

        {/* Active Phase Details Card */}
        <div className="mt-10 rounded-3xl border border-slate-100 bg-soft-grad p-8 shadow-soft sm:p-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              <div className="flex flex-wrap items-center gap-3">
                <ActiveIcon className={`w-7 h-7 shrink-0 ${activeColor}`} />
                <h3 className="text-2xl font-bold text-navy sm:text-3xl">
                  {activePhase.title ? activePhase.title.replace(/^Phase \d+ — /, "") : ""}
                </h3>
                <span className="rounded-full bg-blue-bg px-3.5 py-1 text-xs font-semibold text-primary">
                  {activePhase.sub}
                </span>
              </div>

              {/* Cards Grid */}
              <ul className="mt-8 grid gap-4 sm:grid-cols-3 list-none p-0">
                {(activePhase.items || []).map((it: string, i: number) => (
                  <li
                    key={i}
                    className="rounded-[1.5rem] bg-white p-6 text-sm font-medium text-navy shadow-soft border border-slate-100/80 leading-relaxed"
                  >
                    <span className="text-primary mr-1">0{i + 1}.</span> {it}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom Highlights Row */}
        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {bottom.map((b) => (
            <div
              key={b}
              className="rounded-full bg-blue-bg/60 px-5 py-4 text-center text-sm font-semibold text-navy border border-blue-100/20"
            >
              {b}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
